import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Empty, Table, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { BadgeCheck, CalendarCheck, Clock, Send, UserX } from 'lucide-react';
import Layout from '../../Layout';
import { hrApi, useHr } from './hrStore';
import {
  DOC_KEYS, NO_SHOW_GRACE_DAYS, branchById, candById, daysBetween, fmtD, fmtMoney, missingDocs, noShowCheck, offerNeedsApproval, positionById, reqById, toDay,
  type Candidate, type Employee,
} from './hrLogic';
import { Badge, EmpCell, Kpi, PageHeader, Panel, empUrl, useHrPerms } from './hrUi';
import CandidateDrawer from './CandidateDrawer';
import { NoShowModal } from './RecruitPanels';

/** Offer & nhận việc: offer chờ duyệt / chờ trả lời, người đã nhận offer, người sắp đi làm cần chuẩn bị hồ sơ */
export default function Offers() {
  const s = useHr();
  const navigate = useNavigate();
  const { me, canApprove } = useHrPerms();
  const [params, setParams] = useSearchParams();
  const [noShow, setNoShow] = useState<string | null>(null);
  const id = params.get('id');
  const open = (cid: string | null) => { const n = new URLSearchParams(params); if (cid) n.set('id', cid); else n.delete('id'); setParams(n, { replace: true }); };
  const today = toDay();

  const offers = s.candidates.filter(c => c.outcome === 'open' && (c.offer?.status === 'need_approval' || c.offer?.status === 'sent'))
    .sort((a, b) => (a.offer!.status === 'need_approval' ? -1 : 1) - (b.offer!.status === 'need_approval' ? -1 : 1) || a.offer!.at - b.offer!.at);
  const accepted = s.candidates.filter(c => c.outcome === 'open' && c.offer?.status === 'accepted').sort((a, b) => a.offer!.startDate.localeCompare(b.offer!.startDate));
  // Người tuyển mới: sắp đi làm hoặc mới nhận việc trong 14 ngày
  const starters = s.employees.filter(e => e.candidateId && !e.leftDate && daysBetween(e.hireDate, today) <= 14)
    .sort((a, b) => a.hireDate.localeCompare(b.hireDate));
  const noShows = s.candidates.filter(c => c.noShow && c.noShow.at >= dayjs().subtract(90, 'day').valueOf()).length;
  const pos = (c: Candidate) => { const r = reqById(s, c.requisitionId); return { r, label: `${positionById(s, r?.positionId)?.name || ''} · ${branchById(s, r?.branchId || '')?.short || ''}` }; };

  const who = (c: Candidate) => <div><div className="font-medium">{c.name}</div><div className="text-[11px] text-gray-400">{pos(c).label}</div></div>;
  const startIn = (d: string) => { const n = daysBetween(today, d); return <span className={n < 0 ? 'text-rose-600' : n <= 3 ? 'text-amber-600 font-medium' : ''}>{fmtD(d)} <span className="text-[11px]">({n === 0 ? 'hôm nay' : n > 0 ? `còn ${n} ngày` : `${-n} ngày trước`})</span></span>; };

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Offer & nhận việc" desc="Theo dõi từ lúc lập offer tới khi người mới đi làm: duyệt, nhắc ứng viên trả lời, chuẩn bị hồ sơ, xử lý không đến" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<BadgeCheck size={18} />} tone={offers.some(c => c.offer!.status === 'need_approval') ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-500'} label="Offer chờ duyệt" value={offers.filter(c => c.offer!.status === 'need_approval').length} />
          <Kpi icon={<Send size={18} />} tone="bg-sky-50 text-sky-600" label="Chờ ứng viên trả lời" value={offers.filter(c => c.offer!.status === 'sent').length} />
          <Kpi icon={<CalendarCheck size={18} />} tone="bg-emerald-50 text-emerald-600" label="Sắp / mới nhận việc" value={accepted.length + starters.length} hint="trong 14 ngày" />
          <Kpi icon={<UserX size={18} />} tone="bg-rose-50 text-rose-600" label="Không đến nhận việc" value={noShows} hint="90 ngày qua" />
        </div>

        <Panel title={`Offer đang chờ (${offers.length})`}>
          <Table size="middle" rowKey="id" dataSource={offers} pagination={false} scroll={{ x: 860 }} onRow={c => ({ onClick: () => open(c.id), className: 'cursor-pointer' })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có offer đang chờ" /> }}
            columns={[
              { title: 'Ứng viên', key: 'c', width: 240, render: (_, c) => who(c) },
              { title: 'Lương offer', key: 'sal', width: 200, render: (_, c) => { const { r } = pos(c); return <div>{fmtMoney(c.offer!.salary)}{offerNeedsApproval(r, c.offer!.salary) && <div className="text-[11px] text-amber-600">ngoài khung {r ? `${fmtMoney(r.salaryMin)} – ${fmtMoney(r.salaryMax)}` : ''}</div>}</div>; } },
              { title: 'Ngày nhận việc', key: 'start', width: 170, render: (_, c) => startIn(c.offer!.startDate) },
              { title: 'Trạng thái', key: 'st', width: 200, render: (_, c) => {
                const days = daysBetween(toDay(c.offer!.at), today);
                return c.offer!.status === 'need_approval'
                  ? <Badge className="bg-amber-50 text-amber-700 border-amber-200">Chờ duyệt ngoài khung</Badge>
                  : <div><Badge className="bg-sky-50 text-sky-700 border-sky-200">Đã gửi</Badge><div className={`text-[11px] ${days > 3 ? 'text-rose-600' : 'text-gray-400'}`}>{days ? `${days} ngày chưa trả lời${days > 3 ? ' — gọi nhắc' : ''}` : 'gửi hôm nay'}</div></div>;
              } },
              { title: '', key: 'act', width: 120, render: (_, c) => c.offer!.status === 'need_approval' && canApprove && (
                <Button size="small" type="primary" onClick={e => { e.stopPropagation(); const r = hrApi.approveOffer(c.id, me); if (r.ok) message.success('Đã duyệt offer'); else message.error(r.errors?.join(', ')); }}>Duyệt</Button>
              ) },
            ]} />
        </Panel>

        <Panel title={`Đã nhận offer — chờ làm thủ tục nhận việc (${accepted.length})`}>
          <Table size="middle" rowKey="id" dataSource={accepted} pagination={false} scroll={{ x: 760 }} onRow={c => ({ onClick: () => open(c.id), className: 'cursor-pointer' })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có ai" /> }}
            columns={[
              { title: 'Ứng viên', key: 'c', width: 240, render: (_, c) => who(c) },
              { title: 'Lương', key: 'sal', width: 150, render: (_, c) => fmtMoney(c.offer!.salary) },
              { title: 'Ngày nhận việc', key: 'start', width: 180, render: (_, c) => startIn(c.offer!.startDate) },
              { title: '', key: 'act', width: 190, render: (_, c) => <span className="text-xs text-[#3e91ab]">Tạo hồ sơ nhân sự & thỏa thuận thử việc ›</span> },
            ]} />
        </Panel>

        <Panel title={`Người mới: sắp đi làm / mới nhận việc (${starters.length})`}>
          <Table size="middle" rowKey="id" dataSource={starters} pagination={false} scroll={{ x: 900 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có người mới trong 14 ngày" /> }}
            columns={[
              { title: 'Nhân sự', key: 'e', width: 260, render: (_, e: Employee) => <EmpCell s={s} e={e} /> },
              { title: 'Ngày nhận việc', key: 'd', width: 180, render: (_, e: Employee) => startIn(e.hireDate) },
              { title: 'Chuẩn bị', key: 'prep', width: 280, render: (_, e: Employee) => {
                const miss = missingDocs(s, e);
                const prob = s.contracts.find(c => c.empId === e.id && c.kind === 'probation');
                return (
                  <div className="flex flex-wrap gap-1">
                    {prob && !prob.signedAt && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Thỏa thuận thử việc chưa ký</Badge>}
                    {miss.length > 0 && <Tooltip title={miss.map(k => DOC_KEYS[k]).join(', ')}><span><Badge className="bg-amber-50 text-amber-700 border-amber-200">Thiếu {miss.length} giấy tờ</Badge></span></Tooltip>}
                    {!miss.length && prob?.signedAt && <span className="text-xs text-emerald-600">Đã đủ</span>}
                  </div>
                );
              } },
              { title: '', key: 'act', width: 200, render: (_, e: Employee) => (
                <div className="flex gap-1">
                  <Button size="small" onClick={() => navigate(empUrl(e.id, 'contracts'))}>Hồ sơ</Button>
                  {noShowCheck(s, e, today).ok && <Button size="small" danger onClick={() => setNoShow(e.id)}>Không đến</Button>}
                  {candById(s, e.candidateId) && <Button size="small" type="text" onClick={() => open(e.candidateId!)}>Ứng viên</Button>}
                </div>
              ) },
            ]} />
        </Panel>
        <div className="text-[11px] text-gray-400 flex items-center gap-1"><Clock size={12} />“Không đến” dùng được tới {NO_SHOW_GRACE_DAYS} ngày sau ngày nhận việc, khi chưa phát sinh tài sản, quyết định hay hợp đồng chính thức.</div>
      </div>
      <CandidateDrawer id={id} onClose={() => open(null)} />
      {noShow && <NoShowModal empId={noShow} onClose={() => setNoShow(null)} />}
    </Layout>
  );
}
